import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useMatch, useOutletContext, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Carousel } from '@ui/Carousel/Carousel'
import { CarouselChild } from '@ui/Carousel/CarouselItem'
import { Body1, Heading4, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import {
  SegmentedControl,
  SegmentedControlTab,
} from '@ui/components/SegmentedControl/SegmentedControl'
import { AndroidIcon } from '@ui/icons/Android.icon'
import { CheckIcon } from '@ui/icons/Check.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { IOSIcon } from '@ui/icons/Ios.icon'
import { WebTraderIcon } from '@ui/icons/WebTrader.icon'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/v2/Table/Table'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { AccountGroupType } from '@model/AccountGroupType'
import { PlatformInfoDto } from '@model/PlatformInfoDto'
import {
  PlatformTypeEnum,
  formatOSName,
  formatPlatformName,
  getOperatingSystemTypes,
  getPlatformTypes,
  isAndroidOperatingSystem,
  isIOSOperatingSystem,
  isWebTraderOperatingSystem,
} from '@model/PlatformTypeEnum'
import { selectDefaultTradingAccount } from '@model/TradingAccount'

import { useFetchOne } from '@hooks/useFetch'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import tmtAppDesktop from '../images/TickmillTrader/Images/tmtAppDesktop.webp'
import { PlatformDownloadButton } from './PlatformIcon'
import { VideoCards } from './PlatformsLearnMoreVideo'
import { MissingAccountModal } from './modals/MissingAccount.modal'
import {
  useComparePlatformsData,
  usePlatformDownloadLinksData,
  useTickmillTraderAndroidVideosData,
  useTickmillTraderIOSVideosData,
  useTickmillTraderWebtraderVideosData,
} from './platformsLearnMoreData'

import styles from './PlatformsLearnMore.module.scss'

interface TickmillTraderProps extends PlatformInfoProps {
  onWebTraderClick(): void
}

interface PlatformInfoProps {
  platformInfo: PlatformInfoDto[]
}

interface PlatformParams extends Record<string, string | undefined> {
  deviceType: string
}

const useScrollTo = () => {
  const { scrollIntoView } = useScrollToElementIds()
  const locale = useSessionLanguage()

  const match = useMatch(`/${locale}/dashboard/tools/platforms/:platformType/:deviceType`)
  const platformType = match?.params?.platformType || ''
  const deviceType = match?.params?.deviceType || ''

  const [isInitialLoad, setIsInitialLoad] = useState(true)

  useEffect(() => {
    const platformNames = getPlatformTypes().map((x) => x.shortName)
    if (platformNames.some((name) => platformType.includes(formatPlatformName(name)))) {
      scrollIntoView([ScrollToIds.ToolsPlatformsHeader])
      setIsInitialLoad(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformType])

  useEffect(() => {
    const osNames = getOperatingSystemTypes().map((x) => x.shortName)
    if (osNames.some((name) => deviceType.includes(formatPlatformName(name)))) {
      if (!isInitialLoad) scrollIntoView([ScrollToIds.ToolsPlatformsOSHeader])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType])
}

const useSSOFetch = () => {
  const apiClient = useApiClient(ClientApiClient)

  const callback = useCallback(async () => {
    const activeTAs = await getActiveTradingAccounts()
    if (activeTAs?.items?.length >= 1) {
      const defaultTradingAccount = selectDefaultTradingAccount(activeTAs.items)
      if (defaultTradingAccount?.id) {
        return await apiClient.getTATradeSSOToken(defaultTradingAccount.id)
      }
    }
  }, [])
  const { data, isLoading } = useFetchOne(callback)

  const getActiveTradingAccounts = async () => {
    return await apiClient.getTradingAccounts({
      search: {
        AccountGroupType: {
          value: AccountGroupType.Live,
          operator: Operator.EQUAL,
        },
        Platform: {
          value: PlatformTypeEnum.TickmillTrader,
          operator: Operator.EQUAL,
        },
        Status: {
          value: 1,
          operator: Operator.EQUAL,
        },
      },
    })
  }

  return { ssoToken: data, isLoading }
}

export const PlatformsLearnMorePage: React.FC = () => {
  const { platformInfo = [] } = useOutletContext<TickmillTraderProps>()

  useScrollTo()

  const isMobile = useWindowResize()
  const missingAccountModal = useModal(MissingAccountModal, { platformInfo })

  const { ssoToken } = useSSOFetch()

  const handleWebTraderClick = async () => {
    if (ssoToken?.url) {
      const tab = window.open(ssoToken.url, '_blank', 'noopener,noreferrer')
      if (tab) tab.focus()
    } else {
      missingAccountModal.open()
    }
  }

  return (
    <>
      {isMobile ? (
        <TickmillTraderMobile platformInfo={platformInfo} onWebTraderClick={handleWebTraderClick} />
      ) : (
        <TickmillTraderDesktop
          platformInfo={platformInfo}
          onWebTraderClick={handleWebTraderClick}
        />
      )}
    </>
  )
}

const TickmillTraderMobile: React.FC<TickmillTraderProps> = ({
  platformInfo,
  onWebTraderClick,
}) => {
  const { t } = useTranslation()

  return (
    <>
      <section className={styles.downloadSection}>
        <img src={tmtAppDesktop} className={styles.hasMarginBottom} alt='' />
        <Heading4 isParagraph className={styles.hasMarginBottom} medium>
          {t('ToolsPlatforms.Download Your Tickmill Trader')}
        </Heading4>
        <Body1 className={styles.textSecondary} isParagraph>
          {t('ToolsPlatforms.One of the most powerful trading platforms for mobile devices')}
        </Body1>
        <Body1 className={styles.textSecondary} isParagraph>
          {t('ToolsPlatforms.Trade on the go with Tickmill Trader (for iOS and Android)')}
        </Body1>
        <div className={styles.linksSectionAbout}>
          <div className={styles.linksSection}>
            <DownloadLinks platformInfo={platformInfo} />
          </div>
        </div>
      </section>

      <Title title={t('ToolsPlatforms.Or use the WebTrader and trade from anywhere, anytime')}>
        {t(
          'ToolsPlatforms.Mobile App or WebTrader your access to the global markets, anytime, anywhere'
        )}
      </Title>

      <section className={styles.sectionCompare}>
        <ComparePlatformsTableMobile />

        <div>
          <Body1 isParagraph className={styles.textSecondary}>
            {t(
              'ToolsPlatforms.Use the Tickmill Trader Account to access your Live and Demo account, all in one place!'
            )}
          </Body1>
        </div>

        <div className={styles.linksSectionCompare}>
          <div className={styles.linksSection}>
            <DownloadLinks platformInfo={platformInfo} />
          </div>
        </div>

        <DownloadWebTraderLink
          label={t('ToolsPlatforms.Open WebTrader')}
          platformInfo={platformInfo}
          onWebTraderClick={onWebTraderClick}
        />
      </section>

      <Outlet context={{ platformInfo }} />
    </>
  )
}

const TickmillTraderDesktop: React.FC<TickmillTraderProps> = ({
  platformInfo,
  onWebTraderClick,
}) => {
  const { t } = useTranslation()

  return (
    <div>
      <section className={styles.container}>
        <div className={styles.downloadSection}>
          <div className={styles.textSection}>
            <Heading4 isParagraph medium>
              {t('ToolsPlatforms.Download Your Tickmill Trader')}
            </Heading4>
            <Body1 className={styles.textSecondary} isParagraph>
              {t('ToolsPlatforms.One of the most powerful trading platforms for mobile devices')}
            </Body1>
            <Body1 className={styles.textSecondary} isParagraph>
              {t('ToolsPlatforms.Trade on the go with Tickmill Trader (for iOS and Android)')}
            </Body1>
            <div className={styles.linksSection}>
              <DownloadLinks platformInfo={platformInfo} />
            </div>
          </div>
          <div className={styles.tmtImageContainer}>
            <img src={tmtAppDesktop} alt='' />
          </div>
        </div>
      </section>

      <Title title={t('ToolsPlatforms.Or use the WebTrader and trade from anywhere, anytime')}>
        {t('ToolsPlatforms.Seamless trading on the go with our proprietary platform')}
      </Title>

      <section className={styles.sectionCompare}>
        <ComparePlatformsTableDesktop />

        <div className={styles.linksSectionCompare}>
          <div className={styles.linksSection}>
            <DownloadLinks platformInfo={platformInfo} />
            <div className={styles.wtButton}>
              <DownloadWebTraderLink
                label={t('ToolsPlatforms.Open WebTrader')}
                platformInfo={platformInfo}
                onWebTraderClick={onWebTraderClick}
              />
            </div>
          </div>
        </div>
      </section>

      <Outlet context={{ platformInfo }} />
    </div>
  )
}

export const TMTVideos: React.FC = () => {
  const { t } = useTranslation()
  const { deviceType } = useParams<PlatformParams>()

  const webVideos = useTickmillTraderWebtraderVideosData()
  const androidVideos = useTickmillTraderAndroidVideosData()
  const iosVideos = useTickmillTraderIOSVideosData()

  return (
    <>
      <Title title={t('ToolsPlatforms.Learn more about Tickmill Trader')}>
        {t('ToolsPlatforms.Discover the features and functionalities of the Tickmill Trader app')}
      </Title>

      <div id={ScrollToIds.ToolsPlatformsOSHeader} />
      <Tabs />
      <div>
        {isWebTraderOperatingSystem(deviceType) && <VideoCards data={webVideos} />}
        {isAndroidOperatingSystem(deviceType) && <VideoCards data={androidVideos} />}
        {isIOSOperatingSystem(deviceType) && <VideoCards data={iosVideos} />}
      </div>
    </>
  )
}

const ComparePlatformsTableMobile = () => {
  const { t } = useTranslation()
  const { comparePlatformsData } = useComparePlatformsData()

  const renderCellContent = (value: string | boolean) => {
    if (value === true) return <CheckIcon color='text-success-high-em' size={14} />
    if (value === false) return <CloseIcon color='text-danger-high-em' size={14} />
    return value
  }

  return (
    <Carousel loop interval={10000}>
      <CarouselChild key='wt'>
        <div className={styles.table}>
          <div className={styles.columns}>
            <div className={styles.tableHead}>
              <Heading6 medium>{t('ToolsPlatforms.WebTrader')}</Heading6>
            </div>
            {comparePlatformsData.map((x) => (
              <div key={`wt-${x.name}`} className={styles.column}>
                <div className={styles.columnName}>
                  <Body1>{x.name}</Body1>
                </div>
                <div>{renderCellContent(x.webTrader)}</div>
              </div>
            ))}
          </div>
        </div>
      </CarouselChild>

      <CarouselChild key='app'>
        <div className={styles.table}>
          <div className={styles.columns}>
            <div className={styles.tableHead}>
              <Heading6 medium>{t('ToolsPlatforms.Mobile App')}</Heading6>
            </div>
            {comparePlatformsData.map((x) => (
              <div key={`app-${x.name}`} className={styles.column}>
                <div className={styles.columnName}>
                  <Body1>{x.name}</Body1>
                </div>
                <div>{renderCellContent(x.mobileApp)}</div>
              </div>
            ))}
          </div>
        </div>
      </CarouselChild>
    </Carousel>
  )
}

const ComparePlatformsTableDesktop = () => {
  const { t } = useTranslation()
  const { comparePlatformsData } = useComparePlatformsData()

  const renderCellContent = (value: string | boolean) => {
    if (value === true) return <CheckIcon color='text-success-high-em' size={14} />
    if (value === false) return <CloseIcon color='text-danger-high-em' size={14} />
    return value
  }

  return (
    <div>
      <Table className={styles.table}>
        <TableHeader>
          <TableRow>
            <TableHead align='left' />
            <TableHead align='center'>
              <Heading6 medium>{t('ToolsPlatforms.WebTrader')}</Heading6>
            </TableHead>
            <TableHead align='center'>
              <Heading6 medium>{t('ToolsPlatforms.Mobile App')}</Heading6>
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {comparePlatformsData.map((x) => (
            <TableRow key={x.name}>
              <TableCell align='left'>
                <Body1>{x.name}</Body1>
              </TableCell>
              <TableCell align='center'>
                <Body1>{renderCellContent(x.webTrader)}</Body1>
              </TableCell>
              <TableCell align='center'>
                <Body1>{renderCellContent(x.mobileApp)}</Body1>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div>
        <Body1 isParagraph className={styles.textSecondary}>
          {t(
            'ToolsPlatforms.Use the Tickmill Trader Account to access your Live and Demo account, all in one place!'
          )}
        </Body1>
      </div>
    </div>
  )
}

const DownloadLinks: React.FC<PlatformInfoProps> = ({ platformInfo = [] }) => {
  const { platformDownloadLinksData } = usePlatformDownloadLinksData(platformInfo)

  return (
    <>
      {platformDownloadLinksData.map((x) => (
        <PlatformDownloadButton key={x.link} className={styles.hasMarginRight} to={x.link}>
          {x.icon}
        </PlatformDownloadButton>
      ))}
    </>
  )
}

type DownloadWebTraderLinkProps = TickmillTraderProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }

const DownloadWebTraderLink: React.FC<DownloadWebTraderLinkProps> = (props) => {
  const { onWebTraderClick, label, ...restProps } = props
  return (
    <Button.Primary {...restProps} size='lg' fullWidth onClick={onWebTraderClick}>
      {label}
    </Button.Primary>
  )
}

interface TitleProps {
  id?: string
  title: string
}

const Title: React.FC<PropsWithChildren<TitleProps>> = ({ id, title, children }) => {
  return (
    <section className={styles.aboutSection} id={id}>
      <div className={styles.title}>
        <Heading4 isParagraph medium>
          {title}
        </Heading4>
      </div>
      <Body1 isParagraph className={styles.textSecondary}>
        {children}
      </Body1>
    </section>
  )
}

const Tabs: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const handleTabClick = (osName: string) => {
    const parts = location.pathname.split('/')
    parts[parts.length - 1] = formatOSName(osName)
    navigate(parts.join('/'))
  }

  const tabContent: SegmentedControlTab[] = [
    {
      id: 1,
      name: 'WebTrader',
      onClick: () => handleTabClick('WebTrader'),
      iconLeft: WebTraderIcon,
    },
    { id: 2, name: 'iOS', onClick: () => handleTabClick('iOS'), iconLeft: IOSIcon },
    {
      id: 3,
      name: 'Android',
      onClick: () => handleTabClick('Android'),
      iconLeft: AndroidIcon,
    },
  ]

  return (
    <div className={styles.tabsWrapper}>
      <div className={styles.buttonWrapper}>
        <SegmentedControl size='md' tabs={tabContent} />
      </div>
    </div>
  )
}
